import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, message, Tag, Space, Modal, Form,
    Input, Select, DatePicker, InputNumber, Popconfirm, Typography, Row, Col
} from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { expenseService } from '../../services';
import { useApp } from '../../context/AppContext';
import dayjs from 'dayjs';

const { Option } = Select;
const { TextArea } = Input;
const { Title } = Typography;

const CATEGORIES = [
    'Rent', 'Utilities', 'Salaries', 'Equipment', 'Supplies',
    'Lab Fees', 'Insurance', 'Marketing', 'Maintenance',
    'Software', 'Training', 'Travel', 'Taxes', 'Other'
];

const PAYMENT_METHODS = [
    'Cash', 'Credit Card', 'Debit Card', 'Bank Transfer', 'Check', 'Digital Wallet', 'Other'
];

const Expenses = () => {
    const { t } = useTranslation(['expense', 'common']);
    const { user } = useApp();
    const [form] = Form.useForm();

    const [expenses, setExpenses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingExpense, setEditingExpense] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [filters, setFilters] = useState({});

    const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

    useEffect(() => {
        fetchExpenses();
    }, [pagination.current, filters]);

    const fetchExpenses = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.current,
                limit: pagination.pageSize,
                ...filters
            };
            const response = await expenseService.getAll(params);
            if (response.success) {
                setExpenses(response.data);
                setPagination(prev => ({ ...prev, total: response.total }));
            }
        } catch (error) {
            message.error(t('expense:messages.fetchFailed'));
        } finally {
            setLoading(false);
        }
    };

    const handleCreate = () => {
        setEditingExpense(null);
        form.resetFields();
        form.setFieldsValue({
            date: dayjs(),
            currency: 'USD',
            paymentMethod: 'Cash'
        });
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingExpense(record);
        form.setFieldsValue({
            ...record,
            date: dayjs(record.date)
        });
        setModalVisible(true);
    };

    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            setSubmitting(true);

            const data = {
                ...values,
                date: values.date.toISOString()
            };

            if (editingExpense) {
                await expenseService.update(editingExpense._id, data);
                message.success(t('expense:messages.updateSuccess'));
            } else {
                await expenseService.create(data);
                message.success(t('expense:messages.createSuccess'));
            }

            setModalVisible(false);
            form.resetFields();
            fetchExpenses();
        } catch (error) {
            if (error.errorFields) return;
            message.error(t('expense:messages.saveFailed'));
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await expenseService.delete(id);
            message.success(t('expense:messages.deleteSuccess'));
            fetchExpenses();
        } catch (error) {
            message.error(t('expense:messages.deleteFailed'));
        }
    };

    const handleTableChange = (pag) => {
        setPagination(prev => ({ ...prev, current: pag.current }));
    };

    const columns = [
        {
            title: t('expense:date'),
            dataIndex: 'date',
            key: 'date',
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
            width: 120
        },
        {
            title: t('expense:expenseTitle'),
            dataIndex: 'title',
            key: 'title',
            ellipsis: true
        },
        {
            title: t('expense:category'),
            dataIndex: 'category',
            key: 'category',
            render: (cat) => <Tag color="blue">{t(`expense:categories.${cat}`, cat)}</Tag>,
            filters: CATEGORIES.map(c => ({ text: t(`expense:categories.${c}`, c), value: c })),
            onFilter: (value, record) => record.category === value
        },
        {
            title: t('expense:amount'),
            dataIndex: 'amount',
            key: 'amount',
            render: (amount, record) => {
                const symbol = record.currency === 'IQD' ? 'IQD ' : '$';
                const formatted = record.currency === 'IQD'
                    ? (amount || 0).toLocaleString()
                    : (amount || 0).toFixed(2);
                return <span style={{ color: '#ff4d4f', fontWeight: 'bold' }}>{symbol}{formatted}</span>;
            },
            sorter: (a, b) => a.amount - b.amount
        },
        {
            title: t('expense:currency'),
            dataIndex: 'currency',
            key: 'currency',
            render: (c) => <Tag>{c}</Tag>,
            width: 90
        },
        {
            title: t('expense:paymentMethod'),
            dataIndex: 'paymentMethod',
            key: 'paymentMethod',
            render: (m) => t(`expense:paymentMethods.${m}`, m),
            width: 140
        },
        {
            title: t('expense:actions'),
            key: 'actions',
            width: 120,
            render: (_, record) => (
                <Space>
                    {isAdmin && (
                        <>
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEdit(record)}
                            />
                            <Popconfirm
                                title={t('expense:deleteConfirm')}
                                onConfirm={() => handleDelete(record._id)}
                            >
                                <Button size="small" danger icon={<DeleteOutlined />} />
                            </Popconfirm>
                        </>
                    )}
                </Space>
            )
        }
    ];

    return (
        <div>
            <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
                <Col>
                    <Title level={2}>{t('expense:title')}</Title>
                </Col>
                <Col>
                    <Space>
                        <Select
                            placeholder={t('expense:filterByCategory')}
                            allowClear
                            style={{ width: 180 }}
                            onChange={(val) => setFilters(prev => ({ ...prev, category: val }))}
                        >
                            {CATEGORIES.map(c => (
                                <Option key={c} value={c}>{t(`expense:categories.${c}`, c)}</Option>
                            ))}
                        </Select>
                        <Select
                            placeholder={t('expense:currency')}
                            allowClear
                            style={{ width: 100 }}
                            onChange={(val) => setFilters(prev => ({ ...prev, currency: val }))}
                        >
                            <Option value="USD">USD</Option>
                            <Option value="IQD">IQD</Option>
                        </Select>
                        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                            {t('expense:addExpense')}
                        </Button>
                    </Space>
                </Col>
            </Row>

            <Card bordered={false}>
                <Table
                    dataSource={expenses}
                    columns={columns}
                    loading={loading}
                    rowKey="_id"
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showTotal: (total) => t('expense:totalCount', { count: total })
                    }}
                    onChange={handleTableChange}
                    scroll={{ x: 'max-content' }}
                />
            </Card>

            {/* Create/Edit Modal */}
            <Modal
                title={editingExpense ? t('expense:editExpense') : t('expense:addExpense')}
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                onOk={handleSubmit}
                confirmLoading={submitting}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Form.Item
                        name="title"
                        label={t('expense:expenseTitle')}
                        rules={[{ required: true, message: t('expense:validation.titleRequired') }]}
                    >
                        <Input placeholder={t('expense:placeholders.title')} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="category"
                                label={t('expense:category')}
                                rules={[{ required: true, message: t('expense:validation.categoryRequired') }]}
                            >
                                <Select placeholder={t('expense:placeholders.selectCategory')}>
                                    {CATEGORIES.map(c => (
                                        <Option key={c} value={c}>{t(`expense:categories.${c}`, c)}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="date"
                                label={t('expense:date')}
                                rules={[{ required: true, message: t('expense:validation.dateRequired') }]}
                            >
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="amount"
                                label={t('expense:amount')}
                                rules={[{ required: true, message: t('expense:validation.amountRequired') }]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    placeholder="0.00"
                                />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                name="currency"
                                label={t('expense:currency')}
                                rules={[{ required: true }]}
                            >
                                <Select>
                                    <Option value="USD">USD ($)</Option>
                                    <Option value="IQD">IQD</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="paymentMethod"
                        label={t('expense:paymentMethod')}
                    >
                        <Select placeholder={t('expense:placeholders.selectPaymentMethod')}>
                            {PAYMENT_METHODS.map(m => (
                                <Option key={m} value={m}>{t(`expense:paymentMethods.${m}`, m)}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('expense:description')}
                    >
                        <TextArea rows={3} placeholder={t('expense:placeholders.description')} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item
                                name="isRecurring"
                                label={t('expense:recurring')}
                                valuePropName="checked"
                            >
                                <Select defaultValue={false}>
                                    <Option value={false}>{t('expense:recurringOptions.no')}</Option>
                                    <Option value={true}>{t('expense:recurringOptions.yes')}</Option>
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item
                                noStyle
                                shouldUpdate={(prev, cur) => prev.isRecurring !== cur.isRecurring}
                            >
                                {({ getFieldValue }) =>
                                    getFieldValue('isRecurring') ? (
                                        <Form.Item
                                            name="recurringFrequency"
                                            label={t('expense:frequency')}
                                        >
                                            <Select placeholder={t('expense:placeholders.selectFrequency')}>
                                                <Option value="Weekly">{t('expense:frequencies.Weekly')}</Option>
                                                <Option value="Monthly">{t('expense:frequencies.Monthly')}</Option>
                                                <Option value="Quarterly">{t('expense:frequencies.Quarterly')}</Option>
                                                <Option value="Yearly">{t('expense:frequencies.Yearly')}</Option>
                                            </Select>
                                        </Form.Item>
                                    ) : null
                                }
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default Expenses;
